import * as React from "react";
import { SelectInput, Button, required, useDataProvider, AutocompleteArrayInput } from "react-admin";
import { Container, Grid, Paper, makeStyles, Typography, Box } from "@material-ui/core";
import { Form } from "react-final-form";
import SearchIcon from "@material-ui/icons/Search";
import FilterListIcon from "@material-ui/icons/FilterList";
import { useState } from "react";
import { CustomDatetime } from "../../Components/CustomDatetime";
import { useAuth } from "@airborne/react-admin";
import { ContractStatusChoices, ContractStatusEnum } from "../../Enums/ContractStatusEnum";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    background: "linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)",
    border: "1px solid rgba(0, 0, 0, 0.05)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
  headerIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
    fontSize: 28,
  },
  headerTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: "1.25rem",
  },
  searchButton: {
    marginTop: 6,
    padding: theme.spacing(1.5, 4),
    borderRadius: theme.spacing(1),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "1rem",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
    },
  },
}));

const CockpitFilter = ({ filterParams, setFilterParams }: any) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const [allContracts, setAllContracts] = useState<any[]>([]); // Tüm sözleşmeler (raw data)
  const [contractList, setContractList] = useState([{ contractId: 0, contractName: "ALL", crmContractNumber: "0" }]); // Filtreleme için kullanılacak sözleşme listesi
  const [selectedContractStatus, setSelectedContractStatus] = useState(ContractStatusEnum.All);
  const [selectedCrmContractNumber, setSelectedCrmContractNumber] = useState<string | null>(null);
  const formRef = React.useRef<any>(null);
  const [pendingContractIds, setPendingContractIds] = React.useState<number[] | null>(null);
  const { user } = useAuth();
  const contractCustomerCodeBlackList = ["SSB-KKU-C", "AARUSA-C"];

  // AutocompleteArrayInput kendi iç güncellemesini tamamladıktan sonra form değerini değiştir
  React.useEffect(() => {
    if (pendingContractIds !== null && formRef.current) {
      formRef.current.change("contractId__in", pendingContractIds);
      setPendingContractIds(null);
    }
  }, [pendingContractIds]);

  // Bugünden sonraki tarihleri engelleyen validator
  const maxToday = value => {
    if (!value) return undefined;
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Bugünün sonuna kadar izin ver
    const selectedDate = new Date(value);
    return selectedDate > today ? "Date cannot be in the future" : undefined;
  };

  React.useEffect(() => {
    GetContractList();
  }, []);

  React.useEffect(() => {
    filterContractsByStatus();
  }, [selectedContractStatus, allContracts]);

  const GetContractList = async () => {
    await dataProvider
      .getList("cdmheaders", {
        pagination: { page: 1, perPage: 9999 },
        sort: { field: "customerCode", order: "ASC" },
        filter: user?.isCustomer
          ? { ownerName__eq: user?.ownerCode, contractModuleType__eq: "PBH" }
          : { contractModuleType__eq: "PBH" },
      })
      .then(response => {
        let dataLength: number = response.data.length;
        if (dataLength > 0) {
          let data = response.data.filter(x => !contractCustomerCodeBlackList.includes(x.customerCode));
          setFilterParams(prev => ({ ...prev, contractId__in_actual: data.map(c => c.id) }));
          setAllContracts(data);
        }
      })
      .catch(error => {
        console.log(error);
      });
  };

  const filterContractsByStatus = () => {
    if (allContracts.length === 0) return;

    let filteredData = [...allContracts];

    if (selectedContractStatus === ContractStatusEnum.Active) {
      filteredData = allContracts.filter(x => x.contractStatu === "Active");
    } else if (selectedContractStatus === ContractStatusEnum.Passive) {
      filteredData = allContracts.filter(x => x.contractStatu === "Terminate" || x.contractStatu === "Cancelled");
    }

    let contractsData = filteredData.map(x => {
      return { contractId: Number(x.id), contractName: x.contractName, crmContractNumber: x.crmContractNumber };
    });
    contractsData.sort((a, b) => a.contractName.localeCompare(b.contractName));
    const updatedContractList = [{ contractId: 0, contractName: "ALL", crmContractNumber: "0" }, ...contractsData];
    setContractList(updatedContractList);

    // Sadece contractList'i güncelle, istek atmak için Search butonuna basılmasını bekle
    setFilterParams(prev => ({
      ...prev,
      contractList: updatedContractList,
    }));
  };

  const onSubmit = values => {
    const allContractIds = contractList.filter(c => c.contractId !== 0).map(c => c.contractId);

    // Kullanıcının seçtiği contract ID'leri kontrol et
    // Eğer seçilen ID'ler mevcut contractList'te yoksa temizle
    let validSelectedIds = values?.contractId__in?.filter(id => id === 0 || allContractIds.includes(id)) || [0];

    // Eğer geçerli ID kalmadıysa veya boşsa, ALL seç
    if (validSelectedIds.length === 0) {
      validSelectedIds = [0];
    }

    // Search butonuna basıldığında filtreyi güncelle
    if (validSelectedIds.includes(0)) {
      setFilterParams(prev => ({
        ...prev,
        ...values,
        contractId__in: [0],
        contractId__in_actual: allContractIds,
        contractList: contractList,
      }));
    } else {
      setFilterParams(prev => ({
        ...prev,
        ...values,
        contractId__in: validSelectedIds,
        contractId__in_actual: validSelectedIds,
        contractList: contractList,
      }));
    }
  };

  return (
    <Container maxWidth="xl">
      <Form
        onSubmit={onSubmit}
        keepDirtyOnReinitialize
        initialValues={{
          ...filterParams,
          contractId__eq: filterParams?.contractId__eq || [0],
          contractStatus: selectedContractStatus,
        }}
        render={({ handleSubmit, form, submitting, pristine, values }) => {
          formRef.current = form;

          // Handler fonksiyonu form içinde tanımlanmalı
          const handleContractSelection = (event: any) => {
            const selectedIds = event; // AutocompleteArrayInput direkt array döner

            if (!selectedIds || selectedIds.length === 0) {
              setSelectedCrmContractNumber(null);
              return;
            }

            // ALL (0) kontrolü - ALL her zaman tek başına olmalı
            const hasAll = selectedIds.includes(0);

            if (hasAll) {
              // Eğer sadece ALL seçiliyse
              if (selectedIds.length === 1) {
                setSelectedCrmContractNumber(null);
                return;
              }

              // ALL ile birlikte başka seçimler varsa
              // Son seçilen öğeyi kontrol et
              const lastSelectedId = selectedIds[selectedIds.length - 1];

              if (lastSelectedId === 0) {
                // Son seçilen ALL ise, sadece ALL'ı bırak
                setSelectedCrmContractNumber(null);
                setPendingContractIds([0]);
              } else {
                // Son seçilen ALL değilse, ALL'ı kaldır ve son seçilen crmContractNumber'a ait tümünü seç
                const lastContract = contractList.find(c => c.contractId === lastSelectedId);

                if (lastContract && lastContract.crmContractNumber !== "0") {
                  const filteredIds = contractList
                    .filter(c => c.crmContractNumber === lastContract.crmContractNumber)
                    .map(c => c.contractId);

                  setSelectedCrmContractNumber(lastContract.crmContractNumber);
                  setPendingContractIds(filteredIds);
                }
              }
              return;
            }

            // ALL yoksa, normal crmContractNumber kontrolü
            const selectedContracts = contractList.filter(c => selectedIds.includes(c.contractId));
            const crmNumbers = [...new Set(selectedContracts.map(c => c.crmContractNumber))];

            if (crmNumbers.length > 1) {
              // Birden fazla farklı crmContractNumber varsa
              const lastSelectedId = selectedIds[selectedIds.length - 1];
              const lastContract = contractList.find(c => c.contractId === lastSelectedId);

              if (lastContract) {
                // Sadece son seçilen crmContractNumber'a sahip olanları tut
                const filteredIds = contractList
                  .filter(c => c.crmContractNumber === lastContract.crmContractNumber)
                  .map(c => c.contractId);

                setSelectedCrmContractNumber(lastContract.crmContractNumber);
                setPendingContractIds(filteredIds);
              }
            } else if (crmNumbers.length === 1) {
              // Tek bir crmContractNumber varsa, direkt ayarla
              setSelectedCrmContractNumber(crmNumbers[0]);
            } else {
              setSelectedCrmContractNumber(null);
            }
          };

          return (
            <form onSubmit={handleSubmit} noValidate>
              <Paper className={classes.paper}>
                <Box className={classes.header}>
                  <FilterListIcon className={classes.headerIcon} />
                  <Typography variant="h6" className={classes.headerTitle}>
                    Filter Options
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <AutocompleteArrayInput
                      source="contractId__in"
                      createLabel="Contract List"
                      optionValue="contractId"
                      optionText="contractName"
                      fullWidth
                      variant="outlined"
                      choices={contractList}
                      validate={required()}
                      onChange={handleContractSelection}
                      helperText={
                        selectedCrmContractNumber ? `Selected CRM Contract: ${selectedCrmContractNumber}` : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SelectInput
                      source="contractStatus"
                      choices={ContractStatusChoices}
                      optionText="description"
                      optionValue="value"
                      variant="outlined"
                      fullWidth
                      defaultValue={ContractStatusEnum.All}
                      onChange={event => {
                        const newValue = Number(event.target.value);
                        setSelectedContractStatus(newValue);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <CustomDatetime
                      source="startDate"
                      fullWidth
                      variant="outlined"
                      label="Start Date"
                      validate={[required(), maxToday]}
                      showTime={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <CustomDatetime
                      source="endDate"
                      fullWidth
                      variant="outlined"
                      label="End Date"
                      validate={[required(), maxToday]}
                      showTime={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={submitting}
                      label="Search"
                      size="large"
                      startIcon={<SearchIcon />}
                      className={classes.searchButton}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>
            </form>
          );
        }}
      />
    </Container>
  );
};

export default CockpitFilter;
